/**
 * API Service Module
 *
 * Purpose: Centralized HTTP client service for all API communications with the OrchestrateHQ AI backend.
 *          Provides authentication, request/response handling, error management, and automatic token refresh.
 *
 * Architecture: Singleton service built on Axios with interceptors for authentication,
 *              retry logic, error handling, and request/response transformation.
 *
 * Dependencies: Axios, Authentication types, Error handling utilities
 * Author: Development Team
 * Created: 2025-01-27
 * Last Updated: 2025-08-08
 */

import axios, { AxiosInstance, AxiosError, AxiosResponse, AxiosRequestConfig } from 'axios';
import {
  LoginRequest,
  LoginResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
  ApiResponse,
  User,
} from '../types/auth';
import { env } from '../config/env';

/**
 * Interface for failed request queue items
 *
 * Purpose: Defines structure for queuing failed requests during token refresh
 * Properties: Promise resolve/reject handlers for request retry
 */
interface FailedRequest {
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
}

/**
 * API Service Configuration Interface
 *
 * Purpose: Defines configuration options for API service
 * Properties: Base URL, timeout, retry settings, and debug options
 */
interface ApiServiceConfig {
  baseURL?: string;
  timeout?: number;
  maxRetries?: number;
  retryDelay?: number;
  enableLogging?: boolean;
}

/**
 * Enhanced API Service Class
 *
 * Purpose: Provides comprehensive HTTP client functionality with authentication,
 *          error handling, retry logic, and request/response transformation
 *
 * Architecture: Singleton pattern with configurable interceptors and middleware
 */
class ApiService {
  /** Axios instance for HTTP requests */
  public api: AxiosInstance;

  /** Flag to prevent concurrent token refresh operations */
  private isRefreshing = false;

  /** Queue of failed requests waiting for token refresh */
  private failedQueue: Array<FailedRequest> = [];

  /** Current number of consecutive refresh attempts */
  private refreshAttempts = 0;

  /** Maximum allowed refresh attempts before giving up */
  private readonly MAX_REFRESH_ATTEMPTS = 3;

  /** Service configuration */
  private config: ApiServiceConfig;

  /** Callback for token refresh failures */
  private onTokenRefreshFailure?: () => void;

  /** Flag to prevent multiple callback calls */
  private callbackCalled = false;

  /** Callback for connectivity/network failures (ORCH-285) */
  private onNetworkFailure?: () => void;

  /**
   * Initialize API Service
   *
   * Purpose: Creates and configures Axios instance with interceptors
   * Logic: Sets up base configuration, request/response interceptors, and error handling
   *
   * @param config - Optional service configuration
   */
  constructor(config: ApiServiceConfig = {}) {
    // Store configuration with defaults
    // Environment variables are imported from config/env (mocked in tests)
    this.config = {
      baseURL: env.apiUrl,
      timeout: 10000,
      maxRetries: 3,
      retryDelay: 1000,
      enableLogging: env.isDev,
      ...config,
    };

    // Create Axios instance with configuration
    this.api = axios.create({
      baseURL: this.config.baseURL,
      timeout: this.config.timeout,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      // Enable credentials for cross-origin requests
      withCredentials: false,
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('orchestratehq_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as any;

        // A refresh's own 401 isn't retried by refreshing again: it rejects, so the
        // refresh that sent it clears the tokens and ends the session (ORCH-356)
        if (
          error.response?.status === 401 &&
          !originalRequest?._retry &&
          !originalRequest?._skipAuthRefresh
        ) {
          if (this.isRefreshing) {
            // If already refreshing, queue this request
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            })
              .then(() => {
                return this.api.request(originalRequest!);
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          // Check if we've exceeded max refresh attempts
          if (this.refreshAttempts >= this.MAX_REFRESH_ATTEMPTS) {
            this.clearTokens();

            // Notify callback about token refresh failure
            if (this.onTokenRefreshFailure && !this.callbackCalled) {
              this.callbackCalled = true;
              this.onTokenRefreshFailure();
            }

            return Promise.reject(error);
          }

          originalRequest!._retry = true;
          this.isRefreshing = true;
          this.refreshAttempts++;

          const refreshToken = localStorage.getItem('orchestratehq_refresh_token');
          if (!refreshToken) {
            // Reset isRefreshing before early return to prevent blocking subsequent requests
            this.isRefreshing = false;
            this.clearTokens();

            // Notify callback about token refresh failure
            if (this.onTokenRefreshFailure && !this.callbackCalled) {
              this.callbackCalled = true;
              this.onTokenRefreshFailure();
            }

            return Promise.reject(error);
          }

          try {
            const refreshResponse = await this.refreshToken({ refreshToken });
            localStorage.setItem('orchestratehq_token', refreshResponse.data.token);
            localStorage.setItem('orchestratehq_refresh_token', refreshResponse.data.refreshToken);

            // Reset refresh attempts on successful refresh
            this.refreshAttempts = 0;

            // Retry all queued requests
            this.failedQueue.forEach(({ resolve }) => {
              resolve();
            });
            this.failedQueue = [];

            // Retry the original request
            if (originalRequest) {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${refreshResponse.data.token}`;
              } else {
                originalRequest.headers = { Authorization: `Bearer ${refreshResponse.data.token}` };
              }
              return this.api.request(originalRequest);
            }
          } catch (refreshError) {
            this.failedQueue.forEach(({ reject }) => {
              reject(refreshError);
            });
            this.failedQueue = [];

            // Only a refused refresh ends the session; a network blip or a cold-start
            // timeout leaves the tokens for the next attempt
            if (ApiService.sessionRefused(refreshError)) {
              this.clearTokens();
              if (this.onTokenRefreshFailure && !this.callbackCalled) {
                this.callbackCalled = true;
                this.onTokenRefreshFailure();
              }
            }

            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        // Auto-retry idempotent GET/HEAD requests on transient failures
        // (timeouts, network errors, 5xx, 429). This covers the cold-start
        // case where the first request after a service has been idle / scaled
        // to zero exceeds the 10s axios timeout but subsequent requests
        // succeed against the now-warm service. See ORCH-154.
        if (originalRequest && this.isRetryableError(error)) {
          const method = (originalRequest.method || 'get').toLowerCase();
          if (method === 'get' || method === 'head') {
            const maxRetries = this.config.maxRetries || 0;
            originalRequest._retryAttempt = (originalRequest._retryAttempt || 0) + 1;
            if (originalRequest._retryAttempt <= maxRetries) {
              const delay =
                (this.config.retryDelay || 1000) * Math.pow(2, originalRequest._retryAttempt - 1);
              this.log(
                `Retrying ${method.toUpperCase()} ${originalRequest.url} (attempt ${originalRequest._retryAttempt}/${maxRetries}) after ${delay}ms`
              );
              await this.sleep(delay);
              return this.api.request(originalRequest);
            }
          }
        }

        // Notify the connectivity layer about a transient/systemic failure so
        // a real request failing this way counts toward outage detection the
        // same way a failed health-check poll does. See ORCH-285.
        if (this.isRetryableError(error)) {
          this.onNetworkFailure?.();
        }

        return Promise.reject(error);
      }
    );
  }

  // Clear tokens from localStorage
  private clearTokens(): void {
    localStorage.removeItem('orchestratehq_token');
    localStorage.removeItem('orchestratehq_refresh_token');
    this.refreshAttempts = 0;
  }

  /**
   * Set callback for token refresh failures
   *
   * Purpose: Allows external components to be notified when token refresh fails
   * Logic: Stores callback function to be called when refresh fails
   *
   * @param callback - Function to call when token refresh fails
   */
  public setTokenRefreshFailureCallback(callback: () => void): void {
    this.onTokenRefreshFailure = callback;
    this.callbackCalled = false; // Reset flag when new callback is set
  }

  /**
   * Set callback for connectivity/network failures
   *
   * Purpose: Allows the connectivity layer to be notified whenever a request
   * fails with a transient/systemic error shape (network error, 5xx, 429,
   * timeout), so real user requests count toward outage detection the same
   * way a failed health-check poll does. See ORCH-285.
   *
   * @param callback - Function to call when a retryable error occurs
   */
  public setNetworkFailureCallback(callback: () => void): void {
    this.onNetworkFailure = callback;
  }

  // Reset refresh attempts (called on successful login)
  private resetRefreshAttempts(): void {
    this.refreshAttempts = 0;
  }

  /**
   * Log API requests and responses for debugging
   *
   * Purpose: Provides debugging information for API calls
   * Logic: Logs request/response details in development mode
   *
   * @param message - Log message
   * @param data - Additional data to log
   */
  private log(message: string, data?: any): void {
    if (this.config.enableLogging) {
      console.log(`[ApiService] ${message}`, data || '');
    }
  }

  /**
   * Extract error message from API response
   *
   * Purpose: Provides consistent error message extraction
   * Logic: Handles different error response formats and structures
   *
   * @param error - Axios error object
   * @returns Formatted error message
   */
  private extractErrorMessage(error: AxiosError): string {
    if (error.response?.data) {
      const data = error.response.data as any;
      if (typeof data === 'string') return data;
      if (data.message) return data.message;
      if (data.detail?.message) return data.detail.message;
      if (data.detail && typeof data.detail === 'string') return data.detail;
      if (data.error) return data.error;
    }

    if (error.message) return error.message;

    return 'An unexpected error occurred. Please try again.';
  }

  /**
   * Check if error is retryable
   *
   * Purpose: Determines if a failed request should be retried
   * Logic: Checks error type, status code, and network conditions
   *
   * @param error - Axios error object
   * @returns True if request should be retried
   */
  private isRetryableError(error: AxiosError): boolean {
    // Network errors (no response)
    if (!error.response) return true;

    // Server errors (5xx)
    if (error.response.status >= 500) return true;

    // Rate limiting (429)
    if (error.response.status === 429) return true;

    // Timeout errors
    if (error.code === 'ECONNABORTED') return true;

    return false;
  }

  /**
   * Sleep for specified duration
   *
   * Purpose: Provides delay for retry logic and rate limiting
   * Logic: Returns promise that resolves after specified milliseconds
   *
   * @param ms - Milliseconds to sleep
   * @returns Promise that resolves after delay
   */
  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Retry failed request with exponential backoff
   *
   * Purpose: Implements retry logic for failed requests
   * Logic: Retries request with increasing delays between attempts
   *
   * @param requestFn - Function that makes the HTTP request
   * @param maxRetries - Maximum number of retry attempts
   * @returns Promise resolving to response data
   */
  private async retryRequest<T>(
    requestFn: () => Promise<AxiosResponse<T>>,
    maxRetries: number = this.config.maxRetries || 3
  ): Promise<AxiosResponse<T>> {
    let lastError: AxiosError;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await requestFn();
        this.log(`Request succeeded on attempt ${attempt + 1}`);
        return response;
      } catch (error) {
        lastError = error as AxiosError;

        // Don't retry if this is the last attempt or error is not retryable
        if (attempt === maxRetries || !this.isRetryableError(lastError)) {
          break;
        }

        // Calculate delay with exponential backoff
        const delay = this.config.retryDelay! * Math.pow(2, attempt);
        this.log(
          `Request failed, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries + 1})`
        );

        await this.sleep(delay);
      }
    }

    throw lastError!;
  }

  // =============================================================================
  // AUTHENTICATION METHODS
  // =============================================================================

  /**
   * User login with email and password
   *
   * Purpose: Authenticates user and retrieves access tokens
   * Logic: Sends credentials to login endpoint and handles response
   *
   * @param credentials - User login credentials
   * @returns Promise resolving to login response with tokens and user data
   *
   * @throws {Error} If login fails or credentials are invalid
   *
   * @example
   * ```typescript
   * const response = await apiService.login({
   *   email: 'user@example.com',
   *   password: 'password123',
   *   tenant_id: 'optional-tenant'
   * });
   * console.log(response.data.user);
   * ```
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    this.log('Attempting user login', { email: credentials.email });

    try {
      const response = await this.retryRequest(() =>
        this.api.post<LoginResponse>('/auth/login', credentials)
      );

      this.resetRefreshAttempts();
      this.log('Login successful');

      return response.data;
    } catch (error) {
      const errorMessage = this.extractErrorMessage(error as AxiosError);
      this.log('Login failed', errorMessage);
      throw new Error(errorMessage);
    }
  }

  async selectTenant(tenant_id: string): Promise<LoginResponse> {
    const response = await this.api.post<LoginResponse>('/auth/select-tenant', { tenant_id });
    return response.data;
  }

  async getAvailableTenants(): Promise<ApiResponse<{ data: any[] }>> {
    const response = await this.api.get<ApiResponse<{ data: any[] }>>('/auth/tenants/available');
    return response.data;
  }

  async googleLogin(credential: string): Promise<LoginResponse> {
    const response = await this.api.post<LoginResponse>('/auth/google', { credential });
    this.resetRefreshAttempts(); // Reset on successful login
    return response.data;
  }

  async logout(): Promise<ApiResponse> {
    const response = await this.api.post<ApiResponse>('/auth/logout');
    this.clearTokens();
    return response.data;
  }

  async refreshToken(data: RefreshTokenRequest): Promise<RefreshTokenResponse> {
    const response = await this.api.post<RefreshTokenResponse>('/auth/refresh', data, {
      _skipAuthRefresh: true,
    } as AxiosRequestConfig);
    return response.data;
  }

  /**
   * Renew the session for callers outside axios (Maestro's streaming fetches)
   *
   * Output: the new access token, or null when the session has ended; then the
   *         tokens are cleared and the refresh-failure callback runs, as for axios.
   */
  async renewSession(): Promise<string | null> {
    const refreshToken = localStorage.getItem('orchestratehq_refresh_token');
    try {
      if (!refreshToken) throw new Error('No refresh token');
      const refreshed = await this.refreshToken({ refreshToken });
      localStorage.setItem('orchestratehq_token', refreshed.data.token);
      localStorage.setItem('orchestratehq_refresh_token', refreshed.data.refreshToken);
      return refreshed.data.token;
    } catch (error) {
      if (!refreshToken || ApiService.sessionRefused(error)) {
        this.clearTokens();
        if (this.onTokenRefreshFailure && !this.callbackCalled) {
          this.callbackCalled = true;
          this.onTokenRefreshFailure();
        }
      }
      return null;
    }
  }

  /** Did the server refuse the session (401/403), rather than the request failing? */
  private static sessionRefused(error: unknown): boolean {
    const status = (error as AxiosError)?.response?.status;
    return status === 401 || status === 403;
  }

  async getCurrentUser(): Promise<ApiResponse<User>> {
    const response = await this.api.get<ApiResponse<User>>('/auth/me');
    return response.data;
  }

  // User methods
  async getUsers(): Promise<ApiResponse<{ users: User[] }>> {
    const response = await this.api.get<ApiResponse<{ users: User[] }>>('/users');
    return response.data;
  }

  async getUser(id: string): Promise<ApiResponse<{ user: User }>> {
    const response = await this.api.get<ApiResponse<{ user: User }>>(`/users/${id}`);
    return response.data;
  }

  // Health check
  async healthCheck(): Promise<ApiResponse> {
    const response = await this.api.get<ApiResponse>('/health');
    return response.data;
  }

  // =============================================================================
  // GENERIC HTTP METHODS
  // =============================================================================

  /**
   * Generic GET request
   *
   * Purpose: Performs HTTP GET request with authentication
   * Logic: Uses Axios instance with auth interceptor
   *
   * @param url - Request URL
   * @param config - Optional Axios request config
   * @returns Promise resolving to response data
   */
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.get<T>(url, config);
    return response.data;
  }

  /**
   * Generic POST request
   *
   * Purpose: Performs HTTP POST request with authentication
   * Logic: Uses Axios instance with auth interceptor
   *
   * @param url - Request URL
   * @param data - Request payload
   * @param config - Optional Axios request config
   * @returns Promise resolving to response data
   */
  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.post<T>(url, data, config);
    return response.data;
  }

  /**
   * Generic PUT request
   */
  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.put<T>(url, data, config);
    return response.data;
  }

  /**
   * Generic PATCH request
   *
   * Purpose: Performs HTTP PATCH request with authentication
   * Logic: Uses Axios instance with auth interceptor
   *
   * @param url - Request URL
   * @param data - Request payload
   * @param config - Optional Axios request config
   * @returns Promise resolving to response data
   */
  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.patch<T>(url, data, config);
    return response.data;
  }

  /**
   * Generic DELETE request
   *
   * Purpose: Performs HTTP DELETE request with authentication
   * Logic: Uses Axios instance with auth interceptor
   *
   * @param url - Request URL
   * @param config - Optional Axios request config
   * @returns Promise resolving to response data
   */
  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.delete<T>(url, config);
    return response.data;
  }

  // =============================================================================
  // FILE UPLOAD AND DOWNLOAD METHODS
  // =============================================================================

  /**
   * Upload file with progress tracking
   *
   * Purpose: Uploads file using multipart/form-data with progress callback
   * Logic: Uses XMLHttpRequest for progress events, Axios for auth token
   *
   * @param url - Upload endpoint URL
   * @param formData - FormData containing file and metadata
   * @param onProgress - Optional callback for upload progress (0-100)
   * @returns Promise resolving to upload response
   */
  async uploadFile<T>(
    url: string,
    formData: FormData,
    onProgress?: (progress: number) => void,
    baseURL?: string
  ): Promise<T> {
    const token = localStorage.getItem('orchestratehq_token');
    const base = baseURL !== undefined ? baseURL : this.config.baseURL;
    const fullUrl = `${base}${url}`;

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      if (onProgress) {
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100);
            onProgress(progress);
          }
        };
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch {
            reject(new Error('Invalid JSON response'));
          }
        } else {
          try {
            const errorResponse = JSON.parse(xhr.responseText);
            reject({
              response: { data: errorResponse, status: xhr.status },
              message: errorResponse.detail || errorResponse.message || 'Upload failed',
            });
          } catch {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        }
      };

      xhr.onerror = () => {
        reject(new Error('Network error during upload'));
      };

      xhr.ontimeout = () => {
        reject(new Error('Upload timed out'));
      };

      xhr.open('POST', fullUrl);
      xhr.timeout = 300000; // 5 minutes for file uploads

      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }

      xhr.send(formData);
    });
  }

  /**
   * Download file as Blob
   *
   * Purpose: Downloads binary file content
   * Logic: Uses Axios with responseType 'blob'
   *
   * @param url - Download endpoint URL
   * @returns Promise resolving to Blob
   */
  async getBlob(url: string, config?: AxiosRequestConfig): Promise<Blob> {
    const response = await this.api.get(url, {
      responseType: 'blob',
      ...config,
    });
    return response.data;
  }
}

export { ApiService };
export const apiService = new ApiService();
export default apiService;
